import { type FC } from "react"

import type { BlogPostEditProposedChange } from "@forento/core/models/aiBlogPost"

import AiProposedChanges, { BeforeAfter, ExpandableRow, PlainRow } from "~/components/AiProposedChanges"

type Props = {
	changes: BlogPostEditProposedChange[]
	decision: "applied" | "discarded" | null
	isApplying: boolean
	isDiscarding: boolean
	onApply(): void
	onDiscard(): void
}

const ProposedChanges: FC<Props> = ({ changes, decision, isApplying, isDiscarding, onApply, onDiscard }) => (
	<AiProposedChanges
		changeCount={changes.length}
		decision={decision}
		isApplying={isApplying}
		isDiscarding={isDiscarding}
		onApply={onApply}
		onDiscard={onDiscard}
	>
		{changes.map((change, index) => (
			<ChangeRow key={index} change={change} />
		))}
	</AiProposedChanges>
)

const ChangeRow: FC<{ change: BlogPostEditProposedChange }> = ({ change }) => {
	switch (change.type) {
		case "post-title":
			return <PlainRow label="Title" detail={`"${change.before}" → "${change.after}"`} />
		case "post-slug":
			return (
				<PlainRow label="Post link" detail={`/blog/${change.before} → /blog/${change.after}`}>
					<p className="mt-1 text-xs text-amber-700">The previous link will stop leading to this article.</p>
				</PlainRow>
			)
		case "post-meta-description":
			return (
				<ExpandableRow label="Meta description updated">
					<BeforeAfter before={change.before} after={change.after} />
				</ExpandableRow>
			)
		case "post-excerpt":
			return (
				<ExpandableRow label="Excerpt updated">
					<BeforeAfter before={change.before} after={change.after} />
				</ExpandableRow>
			)
		case "post-tags":
			return (
				<PlainRow
					label="Tags"
					detail={`${change.before.join(", ") || "None"} → ${change.after.join(", ") || "None"}`}
				/>
			)
		case "cover-image-alt":
			return (
				<ExpandableRow label="Cover image alt text updated">
					<BeforeAfter before={change.before} after={change.after} />
				</ExpandableRow>
			)
		case "article-rewrite":
			return (
				<ExpandableRow
					label="Rewrote the article"
					detail={
						change.preservedMediaCount > 0
							? `${change.preservedMediaCount} ${change.preservedMediaCount === 1 ? "image, video or embed" : "images, videos or embeds"} kept`
							: undefined
					}
				>
					<BeforeAfter before={change.before} after={change.after} />
				</ExpandableRow>
			)
	}
}

export default ProposedChanges
